/**
 * The /webinars/<id> detail page (CR-27, the DevHub-style detail view): badges row, title,
 * long-form description, the meta block (time, room slug, lobby note, seats) and the right
 * sidebar card - the Register/Cancel machinery with the «Вы записаны» state, the seats line,
 * the JOIN button (live: straight in; upcoming: opens the room, a lobby_required room holds
 * non-staff in the lobby), the speaker line (the event's organizer, when set) and the
 * recordings chip deep-linking into the room-filtered archive.
 *
 * Data comes from three reads (parallel, one per source): the calendar event (title,
 * description, organizer, capacity), the registration status (roster size + the viewer's
 * state) and the catalog listing (the live presence, the recordings archive, the room's lobby
 * option for the row of this event). The listing may legitimately miss the row (a stale
 * window, an ad-hoc-only catalog): every catalog fact degrades to its neutral default and
 * only the event 404 renders the not-found state.
 */

import { ArrowLeft, CalendarClock, CheckCircle2, Film, MapPin, Radio, Users, Video } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import {
  cancelWebinarRegistration,
  getWebinarRegistration,
  listWebinars,
  registerForWebinar,
  WebinarsApiError,
} from '../api/webinars';
import type { WebinarEntry, WebinarRegistration } from '../api/webinars';
import { getEvent, CalendarApiError, type CalendarEvent } from '../calendar/api';
import {
  browserSsoStorage,
  callWithSsoRecovery,
  loadSsoSession,
  startSsoAutoRefresh,
} from '../auth/sso';
import { showToast, ToastStack } from '../calendar/Toast';
import {
  checkSession,
  forgetSession,
  startCalendarSignIn,
  calendarSsoConfig,
} from '../calendar/signIn';
import { navigateTo } from '../lib/spa';
import type { KeyValueStorage } from '../room/devicePrefs';

export interface WebinarDetailPageProps {
  /** The event id from the path. */
  readonly eventId: number;
  /** Injected for tests; defaults to the browser's shared SSO store (localStorage-backed). */
  readonly storage?: KeyValueStorage | null;
  /** Injected for tests; defaults to the wall clock. */
  readonly now?: Date;
  /** Fetch override for tests; defaults to the global fetch. */
  readonly fetchImpl?: typeof fetch;
}

type DetailState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error' }
  | {
      readonly kind: 'ready';
      readonly event: CalendarEvent;
      readonly registration: WebinarRegistration;
      readonly entry: WebinarEntry | null;
    };

export function WebinarDetailPage({
  eventId,
  storage,
  now,
  fetchImpl,
}: WebinarDetailPageProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const store = useMemo(() => storage ?? browserSsoStorage(), [storage]);
  const at = useMemo(() => now ?? new Date(), [now]);
  const nowMs = useCallback((): number => (now !== undefined ? now.getTime() : Date.now()), [now]);
  const doFetch = fetchImpl ?? globalThis.fetch;

  const [session, setSession] = useState<'signed_in' | 'guest'>(() =>
    store !== null && checkSession(store, at) === 'signed_in' ? 'signed_in' : 'guest',
  );
  const [token, setToken] = useState<string | null>(() =>
    store !== null && checkSession(store, at) === 'signed_in'
      ? (loadSsoSession(store)?.accessToken ?? null)
      : null,
  );
  const [detail, setDetail] = useState<DetailState>({ kind: 'loading' });
  const [pendingAction, setPendingAction] = useState(false);
  const seqRef = useRef(0);

  const handleUnauthorized = useCallback((): void => {
    if (store !== null) {
      forgetSession(store);
    }
    setToken(null);
    setSession('guest');
  }, [store]);

  useEffect(() => {
    if (store === null || session !== 'signed_in') {
      return undefined;
    }
    return startSsoAutoRefresh({
      config: calendarSsoConfig(),
      storage: store,
      fetchImpl: doFetch,
      nowMs,
      onRefreshed: (freshToken) => setToken(freshToken),
      onInvalidGrant: handleUnauthorized,
    });
  }, [store, doFetch, handleUnauthorized, nowMs, session]);

  const refresh = useCallback((): void => {
    const currentToken = token;
    if (currentToken === null) {
      return;
    }
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const withRecovery = <T,>(call: (fresh: string) => Promise<T>): Promise<T> =>
      callWithSsoRecovery(currentToken, call, {
        config: calendarSsoConfig(),
        storage: store,
        fetchImpl: doFetch,
        rejected: (err: unknown): boolean => err instanceof WebinarsApiError && err.status === 401,
        onRefreshed: (freshToken) => setToken(freshToken),
        nowMs,
      });
    Promise.all([
      withRecovery((fresh) => getEvent(fresh, eventId, doFetch)),
      withRecovery((fresh) => getWebinarRegistration(fresh, eventId, doFetch)),
      withRecovery((fresh) => listWebinars(fresh, doFetch)).catch((err: unknown) => {
        // The catalog is an enhancement here (live presence, recordings): its failure
        // degrades the page, it never kills it.
        if (err instanceof WebinarsApiError && err.status === 401) {
          throw err;
        }
        return [];
      }),
    ])
      .then(([event, registration, entries]) => {
        if (seqRef.current !== seq) {
          return;
        }
        setDetail({
          kind: 'ready',
          event,
          registration,
          entry: entries.find((candidate) => candidate.event?.id === eventId) ?? null,
        });
      })
      .catch((err: unknown) => {
        if (seqRef.current !== seq) {
          return;
        }
        if (
          (err instanceof WebinarsApiError && err.status === 401) ||
          (err instanceof CalendarApiError && err.unauthorized)
        ) {
          handleUnauthorized();
          return;
        }
        // Any of the three reads 404ing means the event itself is gone (the registration
        // read 404s with it): the not-found state, never a generic error card.
        const status = err instanceof WebinarsApiError || err instanceof CalendarApiError
          ? err.status
          : 0;
        if (status === 404) {
          setDetail({ kind: 'not_found' });
          return;
        }
        showToast('error', t('webinars.detail.error'));
        setDetail({ kind: 'error' });
      });
  }, [token, eventId, doFetch, store, nowMs, handleUnauthorized, t]);

  useEffect(() => {
    if (session !== 'signed_in') {
      return;
    }
    refresh();
  }, [session, refresh]);

  const handleSignIn = useCallback(async (): Promise<void> => {
    if (store === null) {
      showToast('error', t('sso.notice_storage_blocked'));
      return;
    }
    try {
      await startCalendarSignIn(
        calendarSsoConfig(),
        window.location.origin,
        store,
        window.location,
        `/webinars/${String(eventId)}`,
      );
    } catch {
      // staying on the page; the button retries
    }
  }, [store, t, eventId]);

  const applyRegistration = useCallback((registration: WebinarRegistration): void => {
    setDetail((current) =>
      current.kind === 'ready' ? { ...current, registration } : current,
    );
  }, []);

  const handleRegister = useCallback(async (): Promise<void> => {
    const currentToken = token;
    if (currentToken === null || detail.kind !== 'ready' || pendingAction) {
      return;
    }
    setPendingAction(true);
    try {
      const fresh = await callWithSsoRecovery(
        currentToken,
        (bearer) => registerForWebinar(bearer, eventId, doFetch),
        {
          config: calendarSsoConfig(),
          storage: store,
          fetchImpl: doFetch,
          rejected: (err: unknown): boolean => err instanceof WebinarsApiError && err.status === 401,
          onRefreshed: (freshToken) => setToken(freshToken),
          nowMs,
        },
      );
      applyRegistration(fresh);
      showToast('success', t('webinars.detail.registered_toast'), detail.event.title);
    } catch (err: unknown) {
      if (err instanceof WebinarsApiError && err.status === 409) {
        // Full roster or a duplicate: the page re-reads the honest state, and the toast
        // surfaces the server's verdict («Мест нет» / «Вы записаны»).
        refresh();
        showToast(
          'error',
          err.code === 'already_registered'
            ? t('webinars.detail.already_registered_toast')
            : t('webinars.detail.full_toast'),
        );
      } else if (err instanceof WebinarsApiError && err.status === 401) {
        handleUnauthorized();
      } else {
        showToast('error', t('webinars.detail.action_failed'));
      }
    } finally {
      setPendingAction(false);
    }
  }, [token, eventId, doFetch, store, nowMs, detail, pendingAction, applyRegistration, refresh, handleUnauthorized, t]);

  const handleCancel = useCallback(async (): Promise<void> => {
    const currentToken = token;
    if (currentToken === null || detail.kind !== 'ready' || pendingAction) {
      return;
    }
    setPendingAction(true);
    try {
      // The cancel returns 204 with no body; the follow-up read carries the honest state.
      const fresh = await callWithSsoRecovery(
        currentToken,
        async (bearer) => {
          await cancelWebinarRegistration(bearer, eventId, doFetch);
          return getWebinarRegistration(bearer, eventId, doFetch);
        },
        {
          config: calendarSsoConfig(),
          storage: store,
          fetchImpl: doFetch,
          rejected: (err: unknown): boolean => err instanceof WebinarsApiError && err.status === 401,
          onRefreshed: (freshToken) => setToken(freshToken),
          nowMs,
        },
      );
      applyRegistration(fresh);
      showToast('success', t('webinars.detail.cancelled_toast'), detail.event.title);
    } catch (err: unknown) {
      if (err instanceof WebinarsApiError && err.status === 401) {
        handleUnauthorized();
      } else {
        showToast('error', t('webinars.detail.action_failed'));
      }
    } finally {
      setPendingAction(false);
    }
  }, [token, eventId, doFetch, store, nowMs, detail, pendingAction, applyRegistration, handleUnauthorized, t]);

  if (session === 'guest') {
    return (
      <Shell>
        <div
          className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between"
          data-testid="webinar-detail-gate"
        >
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-foreground">
              {t('webinars.list.gate.title')}
            </p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
              {t('webinars.list.gate.description')}
            </p>
          </div>
          <button
            type="button"
            data-testid="webinar-detail-gate-signin"
            className="shrink-0 rounded-lg bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            onClick={() => {
              void handleSignIn();
            }}
          >
            {t('calendar.gate.sign_in')}
          </button>
        </div>
      </Shell>
    );
  }

  if (detail.kind === 'loading') {
    return (
      <Shell>
        <div className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground" data-testid="webinar-detail-loading">
          {t('common.loading')}
        </div>
      </Shell>
    );
  }

  if (detail.kind === 'not_found') {
    return (
      <Shell>
        <div className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground" data-testid="webinar-detail-not-found">
          {t('webinars.detail.not_found')}
        </div>
      </Shell>
    );
  }

  if (detail.kind === 'error') {
    return (
      <Shell>
        <div role="alert" className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground" data-testid="webinar-detail-error">
          {t('webinars.detail.error')}
        </div>
      </Shell>
    );
  }

  const { event, registration, entry } = detail;
  const nowTime = at.getTime();
  const live = entry?.live ?? null;
  const isLive = live !== null;
  const isPast = !isLive && new Date(event.endsAt).getTime() <= nowTime;
  const hasRecording = (entry?.recordings?.count ?? 0) > 0;
  const registered = registration.me === 'registered';
  const seatsLeft =
    registration.capacity !== null
      ? Math.max(registration.capacity - registration.registered, 0)
      : null;
  const full = seatsLeft !== null && seatsLeft === 0;
  // Registration closes when the event is over; a live or future event still takes registrations.
  const canRegister = !isPast && !registered && !full;

  return (
    <Shell>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* main column */}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            {isLive ? (
              <span
                data-testid="webinar-detail-live-badge"
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-400"
              >
                <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-emerald-500" />
                {t('webinars.list.live_badge')}
              </span>
            ) : null}
            {hasRecording ? (
              <span
                data-testid="webinar-detail-recording-badge"
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                <Film aria-hidden className="size-3" />
                {t('webinars.list.recording_badge')}
              </span>
            ) : null}
            {!isPast && !full ? (
              <span
                data-testid="webinar-detail-registration-badge"
                className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-primary"
              >
                <CalendarClock aria-hidden className="size-3" />
                {t('webinars.list.registration_badge')}
              </span>
            ) : null}
            {!isPast && full ? (
              <span
                data-testid="webinar-detail-full-badge"
                className="rounded-md bg-red-500/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-red-400"
              >
                {t('webinars.list.full_badge')}
              </span>
            ) : null}
          </div>

          <h2
            data-testid="webinar-detail-title"
            className="mt-3 text-[22px] font-semibold leading-tight tracking-tight text-foreground"
          >
            {event.title}
          </h2>

          {event.organizer !== null && event.organizer.length > 0 ? (
            <p className="mt-1.5 text-[13px] text-muted-foreground">
              <span className="font-medium text-foreground">{event.organizer}</span>
            </p>
          ) : null}

          <div className="mt-4 space-y-1.5 text-[13px] text-muted-foreground" data-testid="webinar-detail-meta">
            <p className="flex items-center gap-2 tabular-nums">
              <CalendarClock aria-hidden className="size-3.5 shrink-0" />
              {new Intl.DateTimeFormat(locale, {
                dateStyle: 'long',
                timeStyle: 'short',
              }).format(new Date(event.startsAt))}
              {' – '}
              {new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(event.endsAt))}
            </p>
            <p className="flex items-center gap-2">
              <MapPin aria-hidden className="size-3.5 shrink-0" />
              <span className="font-mono">/r/{event.roomSlug ?? '—'}</span>
              {entry?.room.lobby ? (
                <span className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                  <Radio aria-hidden className="size-3" />
                  {t('webinars.list.lobby_note')}
                </span>
              ) : null}
            </p>
            {isLive ? (
              <p className="flex items-center gap-2 tabular-nums">
                <Users aria-hidden className="size-3.5 shrink-0" />
                {t('webinars.list.audience', { count: live.participants })}
              </p>
            ) : null}
          </div>

          {event.description !== null && event.description.length > 0 ? (
            <div
              data-testid="webinar-detail-description"
              className="mt-5 whitespace-pre-wrap rounded-xl border border-border bg-card p-4 text-[13.5px] leading-relaxed text-foreground"
            >
              {event.description}
            </div>
          ) : (
            <p className="mt-5 text-[13px] italic text-muted-foreground" data-testid="webinar-detail-description-empty">
              {t('webinars.detail.no_description')}
            </p>
          )}

          {hasRecording && entry !== null && entry.recordings !== null ? (
            <div className="mt-5">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-3 text-[12.5px] font-semibold"
                data-testid="webinar-detail-recordings"
                aria-label={t('webinars.list.recordings_aria', { room: entry.room.slug })}
                onClick={() => navigateTo(`/recordings?room=${entry.room.slug}`)}
              >
                <Film aria-hidden className="size-3.5" />
                {t('webinars.list.recordings_count', { count: entry.recordings.count })}
              </Button>
            </div>
          ) : null}
        </div>

        {/* sidebar card */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-xl border border-border bg-card p-4" data-testid="webinar-detail-sidebar">
            <SeatsLine
              seatsLeft={seatsLeft}
              registered={registration.registered}
              capacity={registration.capacity}
            />
            {registered ? (
              <div
                data-testid="webinar-detail-registered-note"
                className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-2 text-[12.5px] font-semibold text-emerald-400"
              >
                <CheckCircle2 aria-hidden className="size-4 shrink-0" />
                {t('webinars.detail.registered_note')}
              </div>
            ) : null}
            <div className="mt-3 space-y-2">
              {registered ? (
                <Button
                  variant="outline"
                  className="h-10 w-full text-[13.5px] font-semibold"
                  data-testid="webinar-detail-cancel"
                  disabled={pendingAction}
                  onClick={() => {
                    void handleCancel();
                  }}
                >
                  {t('webinars.detail.cancel')}
                </Button>
              ) : (
                <Button
                  className="h-10 w-full text-[13.5px] font-semibold"
                  data-testid="webinar-detail-register"
                  disabled={!canRegister || pendingAction}
                  onClick={() => {
                    void handleRegister();
                  }}
                >
                  {full ? t('webinars.detail.full') : t('webinars.detail.register')}
                </Button>
              )}
              {event.roomSlug !== null ? (
                <Button
                  variant={registered || isLive ? 'default' : 'outline'}
                  className="h-10 w-full gap-1.5 text-[13.5px] font-semibold"
                  data-testid="webinar-detail-join"
                  onClick={() => navigateTo(`/r/${event.roomSlug ?? ''}`)}
                >
                  <Video aria-hidden className="size-4" />
                  {isLive ? t('webinars.detail.join_live') : t('webinars.list.join')}
                </Button>
              ) : null}
            </div>
            {event.roomSlug !== null && entry?.room.lobby ? (
              <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
                {t('webinars.detail.lobby_hint')}
              </p>
            ) : null}
          </div>
        </aside>
      </div>

      <ToastStack />
    </Shell>
  );
}

/** The seats line of the sidebar: registered N of M, or the unlimited note. */
function SeatsLine(props: {
  readonly seatsLeft: number | null;
  readonly registered: number;
  readonly capacity: number | null;
}): JSX.Element {
  const { t } = useTranslation();
  if (props.capacity === null) {
    return (
      <p className="text-[12.5px] text-muted-foreground" data-testid="webinar-detail-seats">
        {t('webinars.detail.seats_unlimited', { count: props.registered })}
      </p>
    );
  }
  return (
    <p className="text-[12.5px] tabular-nums text-muted-foreground" data-testid="webinar-detail-seats">
      {props.seatsLeft !== null && props.seatsLeft > 0
        ? t('webinars.detail.seats_left', { count: props.seatsLeft, registered: props.registered, capacity: props.capacity })
        : t('webinars.detail.seats_full', { registered: props.registered, capacity: props.capacity })}
    </p>
  );
}

/** The shared page chrome: header with the back button and the scrollable body. */
function Shell(props: { readonly children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid="webinar-detail-page"
      className="flex h-screen flex-col overflow-hidden bg-background text-foreground"
    >
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 sm:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="size-9 text-muted-foreground hover:text-foreground"
          aria-label={t('webinars.back_aria')}
          data-testid="webinar-detail-back"
          onClick={() => navigateTo('/webinars')}
        >
          <ArrowLeft aria-hidden className="size-4" />
        </Button>
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          {t('webinars.page_title')}
        </h1>
      </header>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[920px] px-4 py-6 sm:px-6 lg:px-8">{props.children}</div>
      </div>
    </div>
  );
}
